import { supabase } from './supabase';
import { ActivityService } from './activity';
import { NotificationService } from './notifications';
import type { Database } from './database.types';

type TeamMember = Database['public']['Tables']['team_members']['Row'];
type TeamMemberInsert = Database['public']['Tables']['team_members']['Insert'];

export interface TeamMemberWithUser extends TeamMember {
  user: {
    id: string;
    name: string;
    email: string;
    avatar?: string;
    team_role?: string;
  };
}

export interface AvailableTeamMember {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  team_role?: string;
}

export interface AssignMemberRequest {
  projectId: string;
  userId: string;
  permissions?: {
    canAnalyze?: boolean;
    canUpload?: boolean;
    canInvite?: boolean;
    canManageProject?: boolean;
  };
}

export interface ProjectMemberInfo {
  id: string;
  userId: string;
  projectId: string;
  role: string;
  permissions: any;
  assignedAt: string;
  assignedBy?: string;
  user: {
    id: string;
    name: string;
    email: string;
    avatar?: string;
    team_role?: string;
  };
}

export class ProjectMemberService {
  /**
   * Get all team members from the user's organization who are not yet assigned to the project
   */
  static async getAvailableTeamMembers(projectId: string): Promise<AvailableTeamMember[]> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      // Get current user's organization
      const { data: currentUser, error: currentUserError } = await supabase
        .from('users')
        .select('organization_id')
        .eq('id', user.id)
        .single();

      if (currentUserError) {
        throw new Error(`Failed to fetch user data: ${currentUserError.message}`);
      }

      // If user has no organization, return empty array (individual users)
      if (!currentUser?.organization_id) {
        return [];
      }

      // Get all users in the organization
      const { data: orgUsers, error: orgUsersError } = await supabase
        .from('users')
        .select('id, name, email, avatar, team_role')
        .eq('organization_id', currentUser.organization_id)
        .neq('id', user.id); // Exclude current user

      if (orgUsersError) {
        throw new Error(`Failed to fetch organization users: ${orgUsersError.message}`);
      }

      // Get users already assigned to this project
      const { data: assignedMembers, error: assignedError } = await supabase
        .from('team_members')
        .select('user_id')
        .eq('project_id', projectId)
        .eq('invitation_status', 'accepted');

      if (assignedError) {
        throw new Error(`Failed to fetch assigned members: ${assignedError.message}`);
      }

      const assignedUserIds = new Set(assignedMembers.map(member => member.user_id));

      // Filter out already assigned users and transform to the correct type
      const availableUsers = (orgUsers || [])
        .filter(user => !assignedUserIds.has(user.id))
        .map(user => ({
          id: user.id,
          name: user.name,
          email: user.email,
          avatar: user.avatar || undefined,
          team_role: user.team_role || undefined
        }));

      return availableUsers;
    } catch (error) {
      console.error('Get available team members error:', error);
      throw error;
    }
  }

  /**
   * Assign an existing team member to a project
   * Role will be inherited from their team_role in the organization
   */
  static async assignMemberToProject(request: AssignMemberRequest): Promise<ProjectMemberInfo> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      // Get current user's organization to validate team assignment
      const { data: currentUserData, error: currentUserError } = await supabase
        .from('users')
        .select('organization_id')
        .eq('id', user.id)
        .single();

      if (currentUserError || !currentUserData?.organization_id) {
        throw new Error('Only users in organizations can assign team members to projects');
      }

      // Get the user's team role to inherit for project assignment
      const { data: targetUser, error: targetUserError } = await supabase
        .from('users')
        .select('team_role, organization_id')
        .eq('id', request.userId)
        .single();

      if (targetUserError || !targetUser) {
        throw new Error('Target user not found');
      }

      // Validate that both users are in the same organization
      if (!targetUser.organization_id || targetUser.organization_id !== currentUserData.organization_id) {
        throw new Error('Can only assign team members from the same organization');
      }

      const inheritedRole = targetUser.team_role || 'viewer';

      // Check if user is already assigned to this project
      const { data: existingMember, error: checkError } = await supabase
        .from('team_members')
        .select('id')
        .eq('project_id', request.projectId)
        .eq('user_id', request.userId)
        .maybeSingle();

      if (checkError) {
        throw new Error(`Failed to check existing membership: ${checkError.message}`);
      }

      if (existingMember) {
        throw new Error('User is already assigned to this project');
      }

      // Set permissions based on inherited team role
      const defaultPermissions = {
        canAnalyze: inheritedRole !== 'viewer',
        canUpload: inheritedRole !== 'viewer',
        canInvite: inheritedRole === 'admin',
        canManageProject: inheritedRole === 'admin'
      };

      const memberData: TeamMemberInsert = {
        project_id: request.projectId,
        user_id: request.userId,
        role: inheritedRole, // Use inherited team role
        permissions: request.permissions || defaultPermissions,
        invitation_status: 'accepted', // Direct assignment, no invitation needed
        invited_by: user.id
      };

      const { data: member, error } = await supabase
        .from('team_members')
        .insert(memberData)
        .select(`
          *,
          users!team_members_user_id_fkey(id, name, email, avatar, team_role)
        `)
        .single();

      if (error) {
        throw new Error(`Failed to assign member: ${error.message}`);
      }

      // Get project name for activity logging
      const { data: project } = await supabase
        .from('projects')
        .select('name')
        .eq('id', request.projectId)
        .maybeSingle();

      // Log activity using the generic logActivity method
      await ActivityService.logActivity({
        projectId: request.projectId,
        activityType: 'team_member_joined',
        targetType: 'team_member',
        targetId: member.id,
        targetName: (member.users as any)?.name || 'Unknown User',
        details: {
          role: inheritedRole,
          assignedBy: user.id,
          inheritedFromTeamRole: true
        }
      });

      // Create notification for project owner about new team member
      try {
        const { data: project, error: projectError } = await supabase
          .from('projects')
          .select('user_id, name')
          .eq('id', request.projectId)
          .single();

        if (!projectError && project && project.user_id !== member.user_id) {
          await NotificationService.createTeamMemberJoinedNotification(
            project.user_id,
            (member.users as any)?.name || 'A team member',
            project.name,
            request.projectId
          );
        }
      } catch (notificationError) {
        console.warn('⚠️ Failed to create team member joined notification:', notificationError);
      }

      return {
        id: member.id,
        userId: member.user_id,
        projectId: member.project_id,
        role: member.role || 'viewer',
        permissions: member.permissions || {},
        assignedAt: member.created_at || new Date().toISOString(),
        assignedBy: member.invited_by || undefined,
        user: {
          id: (member.users as any)?.id || '',
          name: (member.users as any)?.name || '',
          email: (member.users as any)?.email || '',
          avatar: (member.users as any)?.avatar || undefined,
          team_role: (member.users as any)?.team_role || undefined
        }
      };
    } catch (error) {
      console.error('Assign member to project error:', error);
      throw error;
    }
  }

  /**
   * Remove a member from a project
   */
  static async removeMemberFromProject(projectId: string, userId: string): Promise<void> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      // Get member info before deleting for activity log
      const { data: member, error: memberError } = await supabase
        .from('team_members')
        .select(`
          *,
          users!team_members_user_id_fkey(name)
        `)
        .eq('project_id', projectId)
        .eq('user_id', userId)
        .maybeSingle();

      if (memberError) {
        throw new Error(`Failed to check member: ${memberError.message}`);
      }

      if (!member) {
        throw new Error('Member not found or already removed from this project');
      }

      const { error } = await supabase
        .from('team_members')
        .delete()
        .eq('project_id', projectId)
        .eq('user_id', userId);

      if (error) {
        throw new Error(`Failed to remove member: ${error.message}`);
      }

      // Log activity using the generic logActivity method
      await ActivityService.logActivity({
        projectId: projectId,
        activityType: 'team_member_removed',
        targetType: 'team_member',
        targetId: member.id,
        targetName: (member.users as any)?.name || 'Unknown User',
        details: {
          removedBy: user.id,
          previousRole: member.role
        }
      });
    } catch (error) {
      console.error('Remove member error:', error);
      throw error;
    }
  }

  /**
   * Update project member role
   * Note: Roles are inherited from team roles and cannot be changed at project level
   * Users must update team roles in the Team page to change project permissions
   */
  static async updateProjectMemberRole(
    projectId: string,
    userId: string,
    newRole: string,
    permissions?: any
  ): Promise<ProjectMemberInfo> {
    throw new Error(
      'Project roles are inherited from team roles. Please update the user\'s team role in the Team page to change their project permissions.'
    );
  }

  /**
   * Get projects where the current user is assigned as a team member (for "My Projects" filter)
   */
  static async getMyAssignedProjects(limit: number = 50, offset: number = 0) {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      const { data: teamMemberships, error } = await supabase
        .from('team_members')
        .select(`
          project_id,
          role,
          permissions,
          projects!inner(
            *,
            users!projects_user_id_fkey(id, name, email, avatar)
          )
        `)
        .eq('user_id', user.id)
        .eq('invitation_status', 'accepted')
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      if (error) {
        throw error;
      }

      return (teamMemberships || []).map((membership: any) => {
        const project = membership.projects;
        return {
          ...project,
          userRole: membership.role,
          userPermissions: membership.permissions,
          owner: project.users
        };
      });
    } catch (error) {
      console.error('Get my assigned projects error:', error);
      throw error;
    }
  }

  /**
   * Get combined list of owned projects and assigned projects (for complete "My Projects" view)
   */
  static async getAllMyProjects(limit: number = 50, offset: number = 0) {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      // Get owned projects and assigned projects in parallel
      const [ownedProjectsResult, assignedProjects] = await Promise.all([
        supabase
          .from('projects')
          .select(`
            *,
            users!projects_user_id_fkey(id, name, email, avatar)
          `)
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .range(offset, offset + limit - 1),
        this.getMyAssignedProjects(limit, offset)
      ]);

      if (ownedProjectsResult.error) {
        throw new Error(`Failed to fetch owned projects: ${ownedProjectsResult.error.message}`);
      }

      // Combine and deduplicate projects
      const allProjects = [...(ownedProjectsResult.data || []), ...assignedProjects];
      const uniqueProjects = allProjects.filter((project, index, self) => 
        index === self.findIndex(p => p.id === project.id)
      );

      if (uniqueProjects.length === 0) {
        return [];
      }

      // Get activity data and member counts for all projects in a single batch call
      const { ProjectService } = await import('./projects');
      const projectIds = uniqueProjects.map(p => p.id);
      
      // Make both batch calls in parallel for maximum performance
      const [memberCounts, activityData] = await Promise.all([
        ProjectService.getBatchProjectMemberCounts(projectIds),
        ProjectService.getBatchProjectActivity(projectIds)
      ]);

      // Combine project data with batch-fetched info
      const projectsWithActivity = uniqueProjects.map(project => ({
        ...project,
        owner: project.users || project.owner,
        isOwner: project.user_id === user.id,
        userRole: project.userRole || (project.user_id === user.id ? 'owner' : 'member'),
        recentActivity: activityData[project.id] || { creativesCount: 0, analysesCount: 0 },
        memberCount: memberCounts[project.id] || 1
      }));

      // Sort by created_at descending (most recent first)
      return projectsWithActivity.sort((a, b) => {
        const aDate = new Date(a.created_at || 0);
        const bDate = new Date(b.created_at || 0);
        return bDate.getTime() - aDate.getTime();
      });
    } catch (error) {
      console.error('Get all my projects error:', error);
      throw error;
    }
  }
} 